import type { Phase, PlaylistHistory, PhaseHistory, TrackUsageEntry, GeneratedPlaylist } from '../types';

const STORAGE_KEY = 'healfm-playlist-history';
const MAX_HISTORY_SESSIONS = 10;

function createEmptyPhaseHistory(): PhaseHistory {
  return {
    opening: [],
    building: [],
    peak: [],
    longTail: [],
    closing: [],
  };
}

function createEmptyHistory(): PlaylistHistory {
  return {
    version: 1,
    phases: createEmptyPhaseHistory(),
    sessionCount: 0,
  };
}

export function loadPlaylistHistory(): PlaylistHistory {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return createEmptyHistory();
    }

    const parsed = JSON.parse(stored) as PlaylistHistory;

    // Validate version
    if (parsed.version !== 1) {
      return createEmptyHistory();
    }

    return parsed;
  } catch {
    return createEmptyHistory();
  }
}

export function savePlaylistHistory(history: PlaylistHistory): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

export function recordGeneratedPlaylist(playlist: GeneratedPlaylist): void {
  const history = loadPlaylistHistory();
  const now = Date.now();

  // Increment session count
  history.sessionCount++;

  // Add each track's usage to the appropriate phase
  for (const phaseBreakdown of playlist.phaseBreakdown) {
    const phase = phaseBreakdown.phase;
    const phaseHistory = history.phases[phase];

    for (const track of phaseBreakdown.tracks) {
      // Add new entry
      phaseHistory.push({
        isrc: track.isrc,
        usedAt: now,
      });
    }

    // Prune old entries - keep only entries from last MAX_HISTORY_SESSIONS sessions
    // We calculate the cutoff based on session count
    const sessionCutoff = history.sessionCount - MAX_HISTORY_SESSIONS;
    if (sessionCutoff > 0) {
      // Remove entries older than the cutoff
      // Since we can't know exact session numbers from timestamps alone,
      // we'll keep entries from the last N unique timestamps (as a proxy for sessions)
      const uniqueTimestamps = [...new Set(phaseHistory.map(e => e.usedAt))].sort((a, b) => b - a);
      if (uniqueTimestamps.length > MAX_HISTORY_SESSIONS) {
        const oldestAllowedTimestamp = uniqueTimestamps[MAX_HISTORY_SESSIONS - 1];
        history.phases[phase] = phaseHistory.filter(e => e.usedAt >= oldestAllowedTimestamp);
      }
    }
  }

  savePlaylistHistory(history);
}

export function calculateTrackWeight(
  isrc: string,
  phaseHistory: TrackUsageEntry[]
): number {
  // Find the most recent usage of this track
  const trackEntries = phaseHistory.filter(e => e.isrc === isrc);

  if (trackEntries.length === 0) {
    // Never used before - full weight
    return 1.0;
  }

  // Sort by timestamp descending to get most recent
  const sortedByTime = [...trackEntries].sort((a, b) => b.usedAt - a.usedAt);
  const mostRecentTimestamp = sortedByTime[0].usedAt;

  // Count how many unique sessions have happened since this track was used
  // Using timestamps as proxy for sessions
  const uniqueTimestamps = [...new Set(phaseHistory.map(e => e.usedAt))].sort((a, b) => b - a);
  const sessionIndex = uniqueTimestamps.indexOf(mostRecentTimestamp);

  // sessionIndex of 0 means it was used in the most recent session
  // Higher index means it was used longer ago
  const ageInSessions = sessionIndex;

  // Exponential decay: weight approaches 1.0 as sessions pass
  // After 5 sessions: ~0.63 weight
  // After 10 sessions: ~0.86 weight
  const weight = 1 - Math.exp(-ageInSessions / 5);

  // Minimum weight of 0.1 to never fully exclude tracks
  return Math.max(0.1, weight);
}

export function clearPlaylistHistory(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

export function getPhaseHistory(history: PlaylistHistory, phase: Phase): TrackUsageEntry[] {
  return history.phases[phase];
}
